import _get from 'lodash/get'
import React from 'react'
import { useFieldArray, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Text, View } from 'react-native'
import { tv } from 'tailwind-variants'

import { AddedItem } from '@flows-ui/components/AddedItem'
import { ListPopup } from '@flows-ui/components/ListPopUp'
import { useFlowsServicesResponses } from '@flows-ui/services/FlowServicesResponsesContext'
import type { StepContentRendererProps } from '@flows-ui/types'
import { processStepContentElementText } from '@flows-ui/utils/processStepContentElementText'
import { BUTTON_IDS } from '@flows/configs/analytics/constants'
import type {
  AnyStepContentElement,
  EditableList as EditableListProps,
} from '@flows/core/types/stepContentTypes'
import type { FlowValues } from '@flows/core/types/stepTypes'
import CircleButton from '@ui-kit/components/CircleButton'
import InlineNotification from '@ui-kit/components/InlineNotification'
import PhosphorIcon from '@ui-kit/components/PhosphorIcon'
import { body2 } from '@ui-kit/core/typography'
import type { Space } from '@ui-kit/types'

const editableListStyles = tv({
  slots: {
    container: 'flex-grow-1 justify-center',
    startText: [body2({ color: 'grey500' }), 'pb-4'],
    itemsContainer: 'flex items-center justify-items-center gap-y-2',
    circleButtonContainer: 'items-center',
  },
  variants: {
    isEmpty: {
      false: {
        itemsContainer: 'justify-items-start',
      },
    },
  },
})

export const EditableList = <
  TProps extends EditableListProps<any, any, any>,
  TStepContentRendererProps extends StepContentRendererProps<AnyStepContentElement>,
>(
  props: TProps & {
    flowValues: FlowValues
    flowName: string
    StepContentRenderer: (
      props: TStepContentRendererProps & { space?: Space }
    ) => React.ReactElement | null
    stepContentRendererProps: TStepContentRendererProps & { space?: Space }
  }
) => {
  const { control, formState } = useFormContext()
  const { t } = useTranslation()
  const servicesResponses = useFlowsServicesResponses()
  const translateFunction = processStepContentElementText(t, {
    flowValues: props.flowValues,
    servicesResponses,
  })

  const errorMessage = _get(formState.errors, props.name)?.message as string | undefined

  const { fields, remove, update } = useFieldArray({
    control,
    name: props.name,
  })

  const { container, startText, itemsContainer, circleButtonContainer } = editableListStyles({
    isEmpty: fields?.length !== 0,
  })

  const [showAddEditPopup, setShowAddEditPopup] = React.useState(false)
  const [activeItemIndex, setActiveItemIndex] = React.useState(-1)

  const closeModal = () => {
    setActiveItemIndex(-1)
    setShowAddEditPopup(false)
  }

  const upsertItem = async (values: Record<string, any>) => {
    update(activeItemIndex === -1 ? fields.length : activeItemIndex, values)
    closeModal()
  }

  const openModal = () => {
    setShowAddEditPopup(true)
  }

  const removeItem = () => {
    remove(activeItemIndex)
    closeModal()
  }

  const getListItemTitle = (index: number) => {
    if (props.editableListItemTitleKey) {
      const titleValue =
        fields[index][props.editableListItemTitleKey as keyof (typeof fields)[typeof index]]
      return titleValue === undefined ? '' : String(titleValue)
    }

    if (props.title) {
      return `${translateFunction({
        ...props.title,
        values: { index: { type: 'staticValue', value: (index + 1).toString() } },
      })}`
    }
    return ''
  }

  return (
    <View className={container()}>
      {fields?.length ? (
        <View>
          {fields.map((fieldValue: any, index) => {
            return (
              <AddedItem
                key={fieldValue.name}
                item={fieldValue}
                title={getListItemTitle(index)}
                onEdit={() => {
                  setActiveItemIndex(index)
                  setShowAddEditPopup(true)
                }}
              />
            )
          })}
        </View>
      ) : (
        <Text className={startText()}>
          {props.startText ? translateFunction(props.startText) : ''}
        </Text>
      )}
      <View className={itemsContainer()}>
        {(props.maximumItems === undefined || fields.length < props.maximumItems) && (
          <View className={circleButtonContainer()}>
            <CircleButton
              id={BUTTON_IDS.listAddButton}
              borderColor={errorMessage ? 'error' : 'grey200'}
              onPress={() => {
                openModal()
              }}
              icon={<PhosphorIcon name="Plus" size={24} />}
              label={props.addItemLabel !== undefined ? translateFunction(props.addItemLabel) : ''}
            />
          </View>
        )}
        {errorMessage ? (
          <View className={circleButtonContainer()}>
            <InlineNotification variant="error" message={String(errorMessage)} />
          </View>
        ) : null}
      </View>
      {showAddEditPopup && (
        <ListPopup
          visible
          defaultValues={activeItemIndex === -1 ? undefined : fields[activeItemIndex]}
          onSubmit={upsertItem}
          remove={removeItem}
          onClose={closeModal}
          activeIndex={activeItemIndex}
          isEdit={activeItemIndex !== -1}
          title={props.popupConfiguration.title}
          schema={props.popupConfiguration.schema}
          elements={props.popupConfiguration.elements}
          renderContent={(popupFormValues) => (
            <props.StepContentRenderer
              {...props.stepContentRendererProps}
              flowValues={{ ...props.stepContentRendererProps.flowValues, ...popupFormValues }}
              hasMinHeight={false}
              elements={props.popupConfiguration.elements}
            />
          )}
        />
      )}
    </View>
  )
}
